import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
  Typography, Alert, Stack, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Business, ReceiptLong, AccountBalance, Payments, CheckCircle, Article,
  Timeline as TimelineIcon, Print, Edit, Assignment, LocalShipping, Send,
} from '@mui/icons-material';
import { invoiceService, PaymentMethod } from '../../services/invoiceService';
import { StatusDot } from '../../components/common/pageKit';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

const inr = (n: any) => `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;
const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const num = (v: any) => Number(v) || 0;
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_TONE: Record<string, any> = {
  DRAFT: 'neutral', SENT: 'info', PARTIALLY_PAID: 'warning', PAID: 'success', OVERDUE: 'error', CANCELLED: 'neutral',
};
const PAYMENT_METHODS: PaymentMethod[] = ['BANK_TRANSFER', 'UPI', 'CHEQUE', 'CASH', 'CARD'];

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, action, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid size={{ xs: 6, sm: 4 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Grid>
);

const SalesInvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inv, setInv] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ paymentAmount: '', paymentDate: todayISO(), paymentMethod: 'BANK_TRANSFER' as PaymentMethod, referenceNumber: '', notes: '' });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const invoice = await invoiceService.getInvoiceById(Number(id));
      setInv(invoice as any);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openReceipt = () => {
    const outstanding = Math.max(0, num(inv?.totalAmount) - num(inv?.paidAmount));
    setPay({ paymentAmount: outstanding ? String(outstanding) : '', paymentDate: todayISO(), paymentMethod: 'BANK_TRANSFER', referenceNumber: '', notes: '' });
    setPayOpen(true);
  };
  const submitReceipt = async () => {
    if (!id) return;
    const amt = num(pay.paymentAmount);
    if (amt <= 0) { setError('Enter a receipt amount greater than zero.'); return; }
    setBusy(true);
    try {
      await invoiceService.recordPayment(Number(id), {
        paymentAmount: amt, paymentDate: pay.paymentDate, paymentMethod: pay.paymentMethod,
        referenceNumber: pay.referenceNumber.trim() || undefined, notes: pay.notes.trim() || undefined,
      });
      setPayOpen(false);
      await load();
    } catch (e: any) { setError(e.message || 'Failed to record receipt'); }
    finally { setBusy(false); }
  };
  const markSent = async () => {
    if (!id) return;
    setBusy(true);
    try { await invoiceService.updateInvoiceStatus(Number(id), { status: 'SENT' }); await load(); }
    catch (e: any) { setError(e.message || 'Failed to update status'); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  if (error && !inv) return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert></Box>;
  if (!inv) return null;

  const total = num(inv.totalAmount);
  const paid = num(inv.paidAmount);
  const outstanding = Math.max(0, total - paid);
  const isDraft = inv.status === 'DRAFT';
  const isCancelled = inv.status === 'CANCELLED';
  const isPaid = inv.status === 'PAID' || outstanding <= 0;
  const posted = !isDraft; // AR posts to GL when the invoice leaves draft
  const overdueDays = inv.dueDate && outstanding > 0 ? Math.floor((Date.now() - new Date(inv.dueDate).getTime()) / 86400000) : 0;
  const payments = inv.payments || [];

  return (
    <Box sx={{ p: 3 }}>
      <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/sales/invoices')} sx={{ mb: 1.5 }}>Invoices</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header band */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20 }}>{inv.invoiceNumber}</Typography>
            <Typography variant="body2" color="text.secondary">{inv.companyName}{inv.orderNumber ? ` · from ${inv.orderNumber}` : ''}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{inr(total)}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: outstanding > 0 ? 'warning.main' : 'success.main' }}>{inr(outstanding)}</Typography>
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <StatusDot label={String(inv.status || '').replace(/_/g, ' ')} tone={STATUS_TONE[inv.status] || 'neutral'} />
            <Typography variant="caption" color="text.secondary">{fmtDate(inv.invoiceDate)}</Typography>
          </Stack>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {!isCancelled && outstanding > 0 && <Button variant="contained" size="small" startIcon={<Payments />} disabled={busy} onClick={openReceipt}>Record Receipt</Button>}
          {isDraft && <Button variant="outlined" size="small" startIcon={<Send />} disabled={busy} onClick={markSent}>Mark Sent</Button>}
          {isDraft && <Button variant="text" size="small" startIcon={<Edit />} onClick={() => navigate(`/sales/invoices/new?editId=${inv.id}`)}>Edit</Button>}
          <Button variant="text" size="small" startIcon={<Print />} onClick={() => navigate(`/sales/invoices/${inv.id}/print`)}>Print</Button>
          {inv.salesOrderId && <Button variant="text" size="small" startIcon={<Assignment />} onClick={() => navigate(`/sales/orders/${inv.salesOrderId}`)}>View SO {inv.orderNumber || ''}</Button>}
          {inv.deliveryId && <Button variant="text" size="small" startIcon={<LocalShipping />} onClick={() => navigate(`/sales/deliveries/${inv.deliveryId}`)}>View Delivery</Button>}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Summary */}
          <SectionCard title="Summary" icon={<Business fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV label="Customer" value={inv.customerId
                ? <Box component="span" onClick={() => navigate(`/entity/customer/${inv.customerId}`)} sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>{inv.companyName || `Customer #${inv.customerId}`}</Box>
                : (inv.companyName || '—')} />
              <KV label="Customer GSTIN" value={inv.customerGstin || '—'} />
              <KV label="Sales Order" value={inv.orderNumber || '—'} />
              <KV label="Invoice Date" value={fmtDate(inv.invoiceDate)} />
              <KV label="Due Date" value={<>{fmtDate(inv.dueDate)} {overdueDays > 0 && <Chip size="small" color="error" label={`Overdue ${overdueDays}d`} sx={{ height: 18, fontSize: 10, ml: 0.5 }} />}</>} />
              <KV label="Place of Supply" value={inv.customerState || inv.state || '—'} />
            </Grid>
          </SectionCard>

          {/* Line items */}
          <SectionCard title="Line Items" icon={<ReceiptLong fontSize="small" color="primary" />}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>{['Product', 'HSN', 'Qty', 'Rate', 'Disc%', 'GST', 'Amount'].map((h) => (
                    <TableCell key={h} align={['Qty', 'Rate', 'Disc%', 'GST', 'Amount'].includes(h) ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                  ))}</TableRow>
                </TableHead>
                <TableBody>
                  {(inv.items || []).length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>No line items</TableCell></TableRow>}
                  {(inv.items || []).map((it: any) => (
                    <TableRow key={it.id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          onClick={it.productId ? () => navigate(`/entity/material/${it.productId}`) : undefined}
                          sx={{ fontWeight: 600, ...(it.productId ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}) }}
                        >
                          {it.productName || `Product #${it.productId}`}
                        </Typography>
                        {(it.batchNo || it.warehouseName) && <Typography variant="caption" color="text.secondary">{[it.warehouseName, it.batchNo].filter(Boolean).join(' · ')}</Typography>}
                      </TableCell>
                      <TableCell>{it.hsnCode || '—'}</TableCell>
                      <TableCell align="right">{num(it.quantity)} {it.unit || ''}</TableCell>
                      <TableCell align="right">{inr(it.unitPrice)}</TableCell>
                      <TableCell align="right">{num(it.discountPercent) || 0}%</TableCell>
                      <TableCell align="right">{inr(num(it.cgstAmount) + num(it.sgstAmount) + num(it.igstAmount))}<Typography variant="caption" color="text.secondary" display="block">@{num(it.gstRate)}%</Typography></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(it.lineTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>

          {/* Receipts */}
          <SectionCard title="Receipts" icon={<Payments fontSize="small" color="primary" />}
            action={outstanding > 0 && !isCancelled ? <Button size="small" startIcon={<Payments />} onClick={openReceipt}>Record Receipt</Button> : undefined}>
            {payments.length === 0 ? (
              <Alert severity={isPaid ? 'success' : 'info'}>{isPaid ? 'Invoice fully settled.' : 'No receipts recorded yet.'}</Alert>
            ) : (
              <Table size="small">
                <TableHead><TableRow>{['Date', 'Method', 'Reference', 'Recorded By', 'Amount'].map((h) => <TableCell key={h} align={h === 'Amount' ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {payments.map((p: any) => (
                    <TableRow key={p.id}>
                      <TableCell>{fmtDate(p.paymentDate)}</TableCell>
                      <TableCell>{String(p.paymentMethod || '').replace(/_/g, ' ')}</TableCell>
                      <TableCell>{p.referenceNumber || '—'}</TableCell>
                      <TableCell>{p.recordedByName || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(p.paymentAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {/* Tax */}
          <SectionCard title="Tax & Totals" icon={<ReceiptLong fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="Taxable" value={inr(inv.subtotal)} />
              <KV label="CGST" value={inr(inv.cgstAmount)} />
              <KV label="SGST" value={inr(inv.sgstAmount)} />
              <KV label="IGST" value={inr(inv.igstAmount)} />
              {num(inv.tdsAmount) > 0 && <KV label="TDS" value={inr(inv.tdsAmount)} />}
              <KV label="Total" value={<Box component="span" sx={{ fontWeight: 800 }}>{inr(total)}</Box>} />
            </Grid>
            {inv.isExport && <Alert severity="info" sx={{ mt: 1.5 }}>Export invoice{inv.exportType ? ` · ${inv.exportType}` : ''}.</Alert>}
          </SectionCard>

          {/* Accounting */}
          <SectionCard title="Accounting" icon={<AccountBalance fontSize="small" color="primary" />}>
            {posted ? (
              <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
                Posted to the ledger — Dr Accounts Receivable, Cr Sales + Output GST.
                <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/finance/journal')}>View Journal</Button>
              </Alert>
            ) : (
              <Alert severity="info">Draft — posts to the general ledger when the invoice is sent.</Alert>
            )}
          </SectionCard>

          {/* Payment status */}
          <SectionCard title="Collection" icon={<Payments fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="Total" value={inr(total)} />
              <KV label="Received" value={inr(paid)} />
              <KV label="Outstanding" value={<Box component="span" sx={{ color: outstanding > 0 ? 'warning.main' : 'success.main', fontWeight: 700 }}>{inr(outstanding)}</Box>} />
            </Grid>
          </SectionCard>

          <Box sx={{ mb: 2 }}>
            <AttachmentsPanel entityType="sales_invoice" entityId={inv.id} title="Invoice Documents" />
          </Box>

          {/* Lifecycle */}
          <SectionCard title="Lifecycle" icon={<TimelineIcon fontSize="small" color="primary" />}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center"><CheckCircle fontSize="small" color="success" /><Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Created</Typography><Typography variant="caption" color="text.secondary">{fmtDate(inv.createdAt)}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center">{posted ? <CheckCircle fontSize="small" color="success" /> : <Article fontSize="small" color="disabled" />}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Sent &amp; Posted</Typography><Typography variant="caption" color="text.secondary">{posted ? 'Posted to GL' : 'Still draft'}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center">{isPaid ? <CheckCircle fontSize="small" color="success" /> : <Payments fontSize="small" color="disabled" />}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Collected</Typography><Typography variant="caption" color="text.secondary">{isPaid ? 'Settled in full' : `${inr(outstanding)} outstanding`}</Typography></Box></Stack>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Record receipt dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record receipt · {inv.invoiceNumber}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Amount" type="number" value={pay.paymentAmount} onChange={(e) => setPay((p) => ({ ...p, paymentAmount: e.target.value }))} autoFocus
                helperText={`Outstanding ${inr(outstanding)}`} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Receipt Date" type="date" InputLabelProps={{ shrink: true }} value={pay.paymentDate} onChange={(e) => setPay((p) => ({ ...p, paymentDate: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth select label="Method" value={pay.paymentMethod} onChange={(e) => setPay((p) => ({ ...p, paymentMethod: e.target.value as PaymentMethod }))}>
                {PAYMENT_METHODS.map((m) => <MenuItem key={m} value={m}>{m.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Reference / UTR" value={pay.referenceNumber} onChange={(e) => setPay((p) => ({ ...p, referenceNumber: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Notes" value={pay.notes} onChange={(e) => setPay((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={submitReceipt}>Record Receipt</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesInvoiceDetailPage;
